import React, { useEffect, useMemo, useState } from "react";
import { Alert, CircularProgress, Button, TextField } from "@mui/material";
import axios from "axios";
import Hoot from "../../Hoot";
import { normalizeStr, paginate, buildPageButtons, statusBadge } from "../_shared/uiKit";

const styles = `
.block{background:var(--card);border:1px solid var(--border);border-radius:18px;padding:14px 16px;}
.head{display:flex;justify-content:space-between;gap:10px;flex-wrap:wrap;margin-bottom:10px;}
.title{font-weight:900;font-size:1.02rem;}
.sub{color:var(--muted);font-size:.78rem;margin-top:2px;}
.kpiRow{display:flex;gap:10px;flex-wrap:wrap;margin:10px 0 12px;}
.kpi{flex:1;min-width:170px;border:1px solid var(--border);border-radius:16px;padding:10px 12px;background:rgba(148,163,184,0.06);}
.kpiLabel{color:var(--muted);font-size:.74rem;font-weight:850;}
.kpiValue{font-weight:950;font-size:1.05rem;margin-top:2px;}
.grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;}
@media(max-width:992px){.grid{grid-template-columns:1fr;}}
.card{border:1px solid var(--border);border-radius:18px;padding:12px 12px;background:rgba(148,163,184,0.05);}
.table{width:100%;border-collapse:separate;border-spacing:0;font-size:.84rem;}
.table th{text-align:left;padding:.55rem .6rem;font-size:.78rem;font-weight:850;color:var(--muted);border-bottom:1px solid var(--border);}
.table td{padding:.55rem .6rem;border-bottom:1px solid rgba(148,163,184,0.35);vertical-align:middle;}
.table tr:hover{background:rgba(148,163,184,0.06);}
.row{display:flex;gap:10px;flex-wrap:wrap;align-items:center;}
`;

function MyProfile() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [me, setMe] = useState(null);
  const [stats, setStats] = useState(null);
  const [schools, setSchools] = useState([]);
  const [payments, setPayments] = useState([]);

  const [qSchools, setQSchools] = useState("");
  const [pageSchools, setPageSchools] = useState(0);
  const [rowsPerPageSchools, setRowsPerPageSchools] = useState(10);

  const role = normalizeStr(me?.eduall_user_session_role || "admin");

  const load = async () => {
    try {
      setLoading(true);
      setError(null);

      const meRes = await axios.get(Hoot() + "me", { withCredentials: true });
      setMe(meRes.data);

      const stRes = await axios.get(Hoot() + "profile/stats", { withCredentials: true });
      setStats(stRes.data);

      const scRes = await axios.get(Hoot() + "profile/schools", { withCredentials: true });
      setSchools(scRes.data || []);

      // para técnico: histórico RH (últimos 12 meses)
      if (normalizeStr(meRes.data?.eduall_user_session_role) === "tech") {
        const pRes = await axios.get(Hoot() + "profile/hr/payments", { withCredentials: true });
        setPayments(pRes.data || []);
      } else {
        setPayments([]);
      }
    } catch {
      setError("Erro ao carregar perfil.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filteredSchools = useMemo(() => {
    const s = normalizeStr(qSchools);
    return schools.filter((r) => {
      const hay = [r.institute_name, r.institute_code, r.state].map(normalizeStr).join(" ");
      return hay.includes(s);
    });
  }, [schools, qSchools]);

  const pgSchools = useMemo(
    () => paginate(filteredSchools, pageSchools, rowsPerPageSchools),
    [filteredSchools, pageSchools, rowsPerPageSchools]
  );

  useEffect(() => {
    if (pgSchools.page !== pageSchools) setPageSchools(pgSchools.page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pgSchools.pageCount]);

  return (
    <>
      <style>{styles}</style>

      <div className="block">
        <div className="head">
          <div>
            <div className="title">Meu perfil</div>
            <div className="sub">Dados pessoais, desempenho, escolas atribuídas e histórico</div>
          </div>
          <Button className="btn-soft" variant="outlined" onClick={load} disabled={loading}>
            {loading ? <CircularProgress size={18} /> : "Atualizar"}
          </Button>
        </div>

        {error ? <Alert severity="error" className="mb-2">{error}</Alert> : null}

        {me ? (
          <>
            <div className="kpiRow">
              <div className="kpi">
                <div className="kpiLabel">Nome</div>
                <div className="kpiValue">{me.eduall_user_session_name}</div>
              </div>
              <div className="kpi">
                <div className="kpiLabel">Email</div>
                <div className="kpiValue">{me.eduall_user_session_email}</div>
              </div>
              <div className="kpi">
                <div className="kpiLabel">Role</div>
                <div className="kpiValue">{me.eduall_user_session_role || "admin"}</div>
              </div>
              <div className="kpi">
                <div className="kpiLabel">Escolas atribuídas</div>
                <div className="kpiValue">{stats?.schools_count ?? schools.length}</div>
              </div>
            </div>

            <div className="grid">
              <div className="card">
                <div style={{ fontWeight: 950, marginBottom: 8 }}>Resumo</div>
                <div className="small-muted" style={{ lineHeight: 1.8 }}>
                  <div><b style={{ color: "var(--text)" }}>Tickets / tarefas:</b> {stats?.tickets_count ?? 0}</div>
                  <div><b style={{ color: "var(--text)" }}>Feedbacks:</b> {stats?.feedbacks_count ?? 0}</div>
                  <div><b style={{ color: "var(--text)" }}>Docs enviados:</b> {stats?.docs_count ?? 0}</div>
                  {role === "tech" ? (
                    <>
                      <div><b style={{ color: "var(--text)" }}>Pagamentos (12m):</b> {stats?.payments_count ?? payments.length}</div>
                      <div><b style={{ color: "var(--text)" }}>Total líquido (12m):</b> {(stats?.payments_total_net ?? 0).toLocaleString("pt-PT")} Kz</div>
                    </>
                  ) : null}
                </div>
              </div>

              <div className="card">
                <div style={{ fontWeight: 950, marginBottom: 8 }}>Escolas atribuídas</div>

                <div className="row" style={{ marginBottom: 10 }}>
                  <TextField
                    label="Pesquisar escola"
                    size="small"
                    value={qSchools}
                    onChange={(e) => { setQSchools(e.target.value); setPageSchools(0); }}
                    style={{ minWidth: 260, flex: 1 }}
                  />
                  <div className="d-flex align-items-center gap-2">
                    <span className="small-muted">Por página:</span>
                    <select
                      className="form-select form-select-sm"
                      style={{ maxWidth: 90 }}
                      value={rowsPerPageSchools}
                      onChange={(e) => { setRowsPerPageSchools(parseInt(e.target.value, 10)); setPageSchools(0); }}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={30}>30</option>
                    </select>
                  </div>
                </div>

                <div style={{ overflowX: "auto" }}>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Escola</th>
                        <th>Código</th>
                        <th>Província</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pgSchools.slice.map((r) => (
                        <tr key={`${r.institute_code}-${r.id || ""}`}>
                          <td style={{ fontWeight: 900 }}>{r.institute_name}</td>
                          <td>{r.institute_code}</td>
                          <td>{r.state || "—"}</td>
                        </tr>
                      ))}
                      {pgSchools.total === 0 ? (
                        <tr><td colSpan={3} style={{ color: "var(--muted)", fontSize: ".8rem" }}>Sem dados.</td></tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>

                <div className="inst-footer">
                  <div className="small-muted">Mostrando {pgSchools.rangeText} de {pgSchools.total}</div>
                  <nav>
                    <ul className="pagination pagination-sm mb-0">
                      <li className={`page-item ${pgSchools.page === 0 ? "disabled" : ""}`}>
                        <button className="page-link" onClick={() => setPageSchools((p) => Math.max(0, p - 1))}>«</button>
                      </li>
                      {buildPageButtons(pgSchools.page, pgSchools.pageCount).map((p, idx) =>
                        p === "..." ? (
                          <li key={`e-${idx}`} className="page-item disabled"><span className="page-link">…</span></li>
                        ) : (
                          <li key={p} className={`page-item ${pgSchools.page + 1 === p ? "active" : ""}`}>
                            <button className="page-link" onClick={() => setPageSchools(p - 1)}>{p}</button>
                          </li>
                        )
                      )}
                      <li className={`page-item ${pgSchools.page + 1 === pgSchools.pageCount ? "disabled" : ""}`}>
                        <button className="page-link" onClick={() => setPageSchools((p) => Math.min(pgSchools.pageCount - 1, p + 1))}>»</button>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>

              {role === "tech" ? (
                <div className="card" style={{ gridColumn: "1 / -1" }}>
                  <div style={{ fontWeight: 950, marginBottom: 8 }}>Histórico RH (últimos 12 meses)</div>
                  <div style={{ overflowX: "auto" }}>
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Mês</th>
                          <th>Base</th>
                          <th>Bónus</th>
                          <th>Desconto</th>
                          <th>Líquido</th>
                          <th>Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payments.map((p) => {
                          const net = Number(p.base_salary || 0) + Number(p.bonus || 0) - Number(p.discount || 0);
                          return (
                            <tr key={p.id}>
                              <td>{p.ref_month}</td>
                              <td>{Number(p.base_salary || 0).toLocaleString("pt-PT")} Kz</td>
                              <td>{Number(p.bonus || 0).toLocaleString("pt-PT")} Kz</td>
                              <td>{Number(p.discount || 0).toLocaleString("pt-PT")} Kz</td>
                              <td style={{ fontWeight: 900 }}>{net.toLocaleString("pt-PT")} Kz</td>
                              <td><span className={statusBadge(p.status)}>{p.status}</span></td>
                            </tr>
                          );
                        })}
                        {payments.length === 0 ? (
                          <tr><td colSpan={6} style={{ color: "var(--muted)", fontSize: ".8rem" }}>Sem registos.</td></tr>
                        ) : null}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}
            </div>
          </>
        ) : (
          <div className="small-muted">Sem dados.</div>
        )}
      </div>
    </>
  );
}

export default MyProfile;
